/**
 * storeReview.ts: where and how a trainer rates Nexus Dex.
 *
 * TWO DOORS (2026-10-26, after the ASO review of 2026-10-26 found 2 ratings
 * in Australia against a competitor's 11K):
 *
 *   1. THE NATIVE RATING SHEET. StoreKit's requestReview on iOS and Play's
 *      In-App Review on Android, through @capacitor-community/in-app-review.
 *      The stars are tapped inside the app; nobody is sent anywhere. It is
 *      requested after MOMENTS OF SUCCESS only (a collection milestone, a
 *      live scan that added entries), and not on the spot: lib/ratingDwell
 *      waits until the trainer has had a look at the result and paused
 *      (components/RatingMoments drives it). Then only when
 *      lib/firstRunPrompts says the review is this session's one ask: never
 *      on day one, never in a session with another prompt, and ONCE on a
 *      device, ever: a requested sheet is the ask, there is no repeat (owner,
 *      2026-10-26: a trainer who has given their feedback is not asked
 *      again, and the OS never says whether they did). The OS applies
 *      its own throttle on top (StoreKit: three a year) and may decide not to
 *      show the sheet at all; the plugin cannot tell us either way, so the
 *      local gate is the only polite part we control.
 *
 *   2. THE WRITE-REVIEW DEEP LINK. The store's own review page, for the
 *      trainer who chose to rate from Settings (openStoreReview). The App
 *      Store's write-review link opens the review sheet directly; Play's
 *      market:// link opens the listing. On the web the https link is the
 *      same page in a browser.
 *
 * Android was gated off while the Play listing sat on the closed testing
 * track (a review link 404s for the public, testers cannot rate). Play has
 * been public since 2026-10-08, so both doors are open there too.
 */
import { getPlatform } from "./platform";
import {
  claimFirstRunPrompt,
  describeFirstRunBlock,
  resetReviewPrompt,
  whyPromptNotDue,
  type FirstRunPickContext,
} from "./firstRunPrompts";
import { dropRatingMoment } from "./ratingDwell";
import { androidInstallSource } from "./nexusDiag";
import { ratingLog } from "./ratingLog";

export const APP_STORE_ID = "6767402880";
export const APP_STORE_REVIEW_URL = `https://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`;
const APP_STORE_REVIEW_DEEP_LINK = `itms-apps://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`;
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.nexusdex.app";
const PLAY_STORE_DEEP_LINK = "market://details?id=com.nexusdex.app";

/** Play has been public since 2026-10-08: reviews can be left there. */
export const REVIEW_ANDROID_ENABLED = true;

/** Whether this build can send the trainer somewhere to leave a review. */
export function canLeaveStoreReview(): boolean {
  const p = getPlatform();
  if (p === "ios") return true;
  if (p === "android") return REVIEW_ANDROID_ENABLED;
  return false;
}

/** The store name to print in copy ("App Store" / "Google Play"). */
export function storeReviewName(): string {
  return getPlatform() === "android" ? "Google Play" : "App Store";
}

/** Open the review page for this platform's store. No-op where none applies. */
export function openStoreReview(): void {
  if (typeof window === "undefined") return;
  const p = getPlatform();
  if (p === "ios") {
    window.location.href = APP_STORE_REVIEW_DEEP_LINK;
    return;
  }
  if (p === "android" && REVIEW_ANDROID_ENABLED) {
    window.location.href = PLAY_STORE_DEEP_LINK;
    return;
  }
  window.open(APP_STORE_REVIEW_URL, "_blank", "noopener");
}

// ── The native sheet ────────────────────────────────────────────────────────

/** How the ask was delivered: the OS sheet, the store page, or not at all. */
export type NativeRatingOutcome = "native" | "deep-link" | "unavailable";

/**
 * Put the OS rating sheet up: StoreKit on iOS, Play In-App Review on Android.
 * On the web there is no sheet, so the store's write-review page opens
 * instead. "unavailable" when the plugin is not in this build or the OS call
 * failed; nothing is shown then, and the caller should not count it as an ask.
 *
 * The OS may silently decline to show the sheet (its own throttle, a sandbox
 * build on TestFlight); "native" means it was asked for, not that it showed.
 */
export async function requestNativeRating(): Promise<NativeRatingOutcome> {
  const p = getPlatform();
  if (p !== "ios" && p !== "android") {
    openStoreReview();
    return "deep-link";
  }
  try {
    const { Capacitor } = await import("@capacitor/core");
    if (!Capacitor.isPluginAvailable("InAppReview")) return "unavailable";
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
    return "native";
  } catch {
    return "unavailable";
  }
}

// ── The gate ────────────────────────────────────────────────────────────────

/** The success this ask rides on. Logged, so a device report can say which. */
export type RatingMoment = "milestone" | "scan";

/**
 * "skipped": the schedule said no (or this is the web), nothing was shown.
 * "sideload": an Android build Play did not install, so Play would not have
 * shown its dialog; nothing was asked for and nothing claimed.
 */
export type RatingAskOutcome = NativeRatingOutcome | "skipped" | "sideload";

/**
 * Why the rating sheet may not be asked for right now, in a short phrase for
 * the log, or null when it may: a store this build can be rated on, and
 * lib/firstRunPrompts naming the review as this session's one optional ask
 * (its floors met, no other prompt this session or within the twenty-hour
 * gap, and never asked for on this device before).
 */
export function whyRatingNotDue(ctx: FirstRunPickContext): string | null {
  if (!canLeaveStoreReview()) return "no store review on this platform";
  const block = whyPromptNotDue("review", ctx);
  return block ? describeFirstRunBlock(block) : null;
}

/** Whether the rating sheet may be asked for right now (whyRatingNotDue is null). */
export function ratingDue(ctx: FirstRunPickContext): boolean {
  return whyRatingNotDue(ctx) == null;
}

/**
 * The look has happened: ask for the rating if it is due. Claims the
 * session's ask only when the OS sheet was actually requested, so a build
 * without the plugin never burns the trainer's one prompt on nothing. Every
 * way out logs its reason (lib/ratingLog).
 *
 * ANDROID AND THE INSTALLER (2026-10-26). Play In-App Review shows its dialog
 * only for an app Play itself installed; for a sideloaded APK (adb, a file
 * manager) the plugin call resolves exactly as it does after a real sheet,
 * and the API never says which. The owner's Galaxy test was such a build: the
 * call resolved, the ask was claimed, the device's one ask was burnt, and no
 * sheet was ever possible. So where the native side can say who installed
 * the app (lib/nexusDiag, NexusDiag plugin), a non-Play install asks for
 * nothing and claims nothing. Where it cannot (a native build from before
 * the plugin, every store build at the time of writing), the call is made as
 * before and a resolved call still counts as asked: on such a build a
 * sideloaded APK burns the one ask, and the log says so. Settings has an
 * admin reset for exactly that (resetRatingAsk).
 */
export async function requestRatingAtMoment(moment: RatingMoment, ctx: FirstRunPickContext): Promise<RatingAskOutcome> {
  const why = whyRatingNotDue(ctx);
  if (why) {
    ratingLog(`skipped at ${moment}: ${why}`);
    return "skipped";
  }
  if (getPlatform() === "android") {
    const source = await androidInstallSource();
    if (source && !source.fromPlay) {
      ratingLog(
        `not asked at ${moment}: Android build installed by ${source.installer ?? "none (adb)"}, not Play; ` +
          "Play shows the sheet only for its own installs, nothing claimed",
      );
      return "sideload";
    }
    if (!source) {
      ratingLog("install source unknown (native side predates NexusDiag): a sideloaded build burns the device's one ask");
    }
  }
  const outcome = await requestNativeRating();
  if (outcome === "native") {
    claimFirstRunPrompt("review");
    ratingLog(`sheet requested at ${moment}; the OS decides whether it shows; never again on this device`);
  } else if (outcome === "unavailable") {
    ratingLog(`not asked at ${moment}: plugin missing or the OS call failed, nothing claimed`);
  }
  return outcome;
}

/**
 * Admin only (Settings): forget this device was ever asked, and drop any
 * pending moment, so the next scan or milestone may ask again. For testing
 * the sheet on a device after its one ask.
 */
export function resetRatingAsk(): void {
  resetReviewPrompt();
  dropRatingMoment("reset");
  ratingLog("reset: the review's asked, shown and done marks and the old card's flag are cleared");
}
